import React from 'react';
import ReactECharts from 'echarts-for-react';
import { Spinner, SpinnerSize } from '@fluentui/react';
import {
    ArrowTrendingRegular,
    ClockRegular,
    MoneyRegular,
    WarningRegular,
    PeopleRegular
} from '@fluentui/react-icons';

import {
    CashflowSeriesPointViewModel,
    DebtSeriesPointViewModel,
    PaymentDelaySeriesPointViewModel,
    TopEntityStatisticViewModel
} from '../../../models/Entities/CashFlow/Statistics/StatisticsModels';
import * as Functions from './FinancialPanel.Functions';
import { chartTooltip, useChartColors, useEchartsTheme } from '../chartTheme';

interface IProps {
    currency: string;
    health: Functions.CashHealth;
    aging: Functions.AgingRow[];
    series: CashflowSeriesPointViewModel[];
    debtSeries: DebtSeriesPointViewModel[];
    delaySeries: PaymentDelaySeriesPointViewModel[];
    topDebtors: TopEntityStatisticViewModel[];
    resolveEntityName: (identifier?: string | null) => string;
    isLoading: boolean;
}


/**
 * Bloque 1 — Caja. Contesta "¿tengo la plata o me la deben?".
 *
 * Primero el estado: cuánto entró, cuánto falta cobrar y qué tan vieja es esa deuda.
 * Después el movimiento en el tiempo, y al final quién debe. La antigüedad va antes
 * que las series porque una deuda de más de 90 días es una decisión a tomar hoy, no
 * una tendencia a observar.
 */
export const CashBlockComponent: React.FC<IProps> = ({
    currency,
    health,
    aging,
    series,
    debtSeries,
    delaySeries,
    topDebtors,
    resolveEntityName,
    isLoading
}) => {
    const money = (value: number) => Functions.formatMoney(value, currency);
    const compact = (value: number) => Functions.formatCompactMoney(value, currency);
    // Colores del tema activo: ingresos verde, egresos naranja, neutro azul, riesgo rojo.
    const colors = useChartColors();
    const chartTheme = useEchartsTheme();
    const INCOME = colors.success;
    const OUTCOME = colors.negative;
    const NEUTRAL = colors.primary;
    const RISK = colors.danger;
    const tooltip = chartTooltip(colors);

    const seriesOption = {
        tooltip: { trigger: 'axis', ...tooltip },
        legend: { data: ['Ingresos', 'Egresos', 'Neto acumulado'], bottom: 0, textStyle: { fontSize: 11 } },
        grid: { left: 12, right: 12, top: 16, bottom: 40, containLabel: true },
        xAxis: { type: 'category', data: series.map((point) => point.periodLabel), axisLabel: { fontSize: 11 } },
        yAxis: [
            { type: 'value', axisLabel: { fontSize: 10, formatter: (value: number) => compact(value) }, splitLine: { lineStyle: { color: colors.gridLine } } },
            { type: 'value', axisLabel: { fontSize: 10, formatter: (value: number) => compact(value) }, splitLine: { show: false } }
        ],
        series: [
            { name: 'Ingresos', type: 'bar', barMaxWidth: 22, itemStyle: { color: INCOME, borderRadius: [3, 3, 0, 0] }, data: series.map((point) => point.income) },
            { name: 'Egresos', type: 'bar', barMaxWidth: 22, itemStyle: { color: OUTCOME, borderRadius: [3, 3, 0, 0] }, data: series.map((point) => point.outcome) },
            { name: 'Neto acumulado', type: 'line', yAxisIndex: 1, smooth: true, symbolSize: 6, itemStyle: { color: NEUTRAL }, data: series.map((point) => point.cumulativeNet) }
        ]
    };

    const agingOption = {
        tooltip: {
            trigger: 'item',
            ...tooltip,
            formatter: (params: any) => {
                const row = aging[params.dataIndex];
                return `<b>${row.label}</b><br/>${money(row.amount)}<br/>${row.debtCount} deudas · ${row.share}% del pendiente`;
            }
        },
        grid: { left: 12, right: 72, top: 8, bottom: 8, containLabel: true },
        xAxis: { type: 'value', axisLabel: { fontSize: 10, formatter: (value: number) => compact(value) }, splitLine: { lineStyle: { color: colors.gridLine } } },
        yAxis: { type: 'category', data: aging.map((row) => row.label).reverse(), axisTick: { show: false }, axisLabel: { fontSize: 11 } },
        series: [
            {
                type: 'bar',
                barMaxWidth: 26,
                data: aging
                    .slice()
                    .reverse()
                    .map((row) => ({ value: row.amount, itemStyle: { color: row.isRisk ? RISK : NEUTRAL, borderRadius: [0, 4, 4, 0] } })),
                label: { show: true, position: 'right', fontSize: 11, color: colors.textSecondary, formatter: (params: any) => compact(params.value) }
            }
        ]
    };

    const debtOption = {
        tooltip: { trigger: 'axis', ...tooltip },
        legend: { data: ['Nueva deuda', 'Cobrado', 'Pendiente acumulado', 'Tasa de recupero'], bottom: 0, textStyle: { fontSize: 11 } },
        grid: { left: 12, right: 12, top: 16, bottom: 40, containLabel: true },
        xAxis: { type: 'category', data: debtSeries.map((point) => point.periodLabel), axisLabel: { fontSize: 11 } },
        yAxis: [
            { type: 'value', axisLabel: { fontSize: 10, formatter: (value: number) => compact(value) }, splitLine: { lineStyle: { color: colors.gridLine } } },
            { type: 'value', min: 0, max: 100, axisLabel: { fontSize: 10, formatter: '{value}%' }, splitLine: { show: false } }
        ],
        series: [
            { name: 'Nueva deuda', type: 'bar', barMaxWidth: 20, itemStyle: { color: OUTCOME, borderRadius: [3, 3, 0, 0] }, data: debtSeries.map((point) => point.newDebtAmount) },
            { name: 'Cobrado', type: 'bar', barMaxWidth: 20, itemStyle: { color: INCOME, borderRadius: [3, 3, 0, 0] }, data: debtSeries.map((point) => point.paidAmount) },
            { name: 'Pendiente acumulado', type: 'line', smooth: true, symbolSize: 5, itemStyle: { color: RISK }, data: debtSeries.map((point) => point.cumulativePendingAmount) },
            { name: 'Tasa de recupero', type: 'line', yAxisIndex: 1, smooth: true, symbolSize: 5, lineStyle: { type: 'dashed' }, itemStyle: { color: NEUTRAL }, data: debtSeries.map((point) => point.settlementRate) }
        ]
    };

    const delayOption = {
        tooltip: {
            trigger: 'axis',
            ...tooltip,
            formatter: (params: any[]) => {
                const point = delaySeries[params[0].dataIndex];
                return `<b>${point.periodLabel}</b><br/>Promedio: <b>${Functions.formatDays(point.averageDelayDays)}</b><br/>` +
                    `Rango: ${Functions.formatDays(point.minDelayDays)} a ${Functions.formatDays(point.maxDelayDays)}<br/>` +
                    `${point.imputationsCount} cobros`;
            }
        },
        grid: { left: 12, right: 12, top: 16, bottom: 24, containLabel: true },
        xAxis: { type: 'category', data: delaySeries.map((point) => point.periodLabel), axisLabel: { fontSize: 11 } },
        yAxis: { type: 'value', axisLabel: { fontSize: 10, formatter: '{value} d' }, splitLine: { lineStyle: { color: colors.gridLine } } },
        series: [
            {
                name: 'Demora de cobro',
                type: 'line',
                smooth: true,
                symbolSize: 6,
                itemStyle: { color: NEUTRAL },
                areaStyle: { opacity: 0.12 },
                data: delaySeries.map((point) => point.averageDelayDays)
            }
        ]
    };

    const pendingTotal = aging.reduce((acc, row) => acc + row.amount, 0) || health.debtPending;

    if (isLoading) {
        return (
            <div className="cs-loading">
                <Spinner size={SpinnerSize.medium} />
                <span>Cargando la caja del centro…</span>
            </div>
        );
    }

    return (
        <>
            {/* ── Estado de la caja ── */}
            <div className="cs-card">
                <div className="cs-card__header">
                    <div className="cs-card__title">Estado de la caja</div>
                    <div className="cs-card__desc">
                        Dinero cobrado y deuda pendiente del período, en {currency}. Sale del sistema de cobranzas: sólo
                        cuenta lo que tiene un pago o una deuda registrada.
                    </div>
                </div>

                <div className="cs-kpi-grid">
                    <div className="cs-kpi-cell">
                        <div className="cs-kpi-cell__icon cs-kpi-cell__icon--green"><ArrowTrendingRegular /></div>
                        <div className="cs-kpi-cell__value">{money(health.collected)}</div>
                        <div className="cs-kpi-cell__label">Cobrado</div>
                        <div className="cs-kpi-cell__desc">Pagos recibidos en el período</div>
                        {health.unimputed > 0 && (
                            <div className="cs-kpi-cell__sub">{money(health.unimputed)} todavía sin imputar a una deuda</div>
                        )}
                    </div>
                    <div className="cs-kpi-cell">
                        <div className="cs-kpi-cell__icon cs-kpi-cell__icon--orange"><MoneyRegular /></div>
                        <div className="cs-kpi-cell__value">{money(health.debtPending)}</div>
                        <div className="cs-kpi-cell__label">Te deben</div>
                        <div className="cs-kpi-cell__desc">Deuda generada y todavía no cobrada</div>
                        <div className="cs-kpi-cell__sub">{health.pendingDebtCount} de {health.debtCount} deudas abiertas</div>
                    </div>
                    <div className="cs-kpi-cell">
                        <div className="cs-kpi-cell__icon cs-kpi-cell__icon--orange"><WarningRegular /></div>
                        <div className="cs-kpi-cell__value">
                            <span className={health.overdueAmount > 0 ? 'fp-amount--negative' : undefined}>
                                {money(health.overdueAmount)}
                            </span>
                        </div>
                        <div className="cs-kpi-cell__label">Vencido hace más de 90 días</div>
                        <div className="cs-kpi-cell__desc">La parte de la deuda que ya es difícil de cobrar</div>
                        <div className="cs-kpi-cell__sub">{health.overdueShare}% de todo lo pendiente</div>
                    </div>
                    <div className="cs-kpi-cell">
                        <div className="cs-kpi-cell__icon cs-kpi-cell__icon--blue"><ClockRegular /></div>
                        <div className="cs-kpi-cell__value">
                            {health.averageDelayDays === undefined ? '—' : Functions.formatDays(health.averageDelayDays)}
                        </div>
                        <div className="cs-kpi-cell__label">Demora de cobro</div>
                        <div className="cs-kpi-cell__desc">Cuánto tarda en cobrarse una deuda, en promedio</div>
                        {health.averageDelayDays !== undefined && health.maxDelayDays !== undefined && (
                            <div className="cs-kpi-cell__sub">
                                Entre {Functions.formatDays(health.minDelayDays ?? 0)} y {Functions.formatDays(health.maxDelayDays)}
                            </div>
                        )}
                    </div>
                    <div className="cs-kpi-cell">
                        <div className="cs-kpi-cell__icon cs-kpi-cell__icon--green"><PeopleRegular /></div>
                        <div className="cs-kpi-cell__value">{Functions.formatPercent(health.recoveryRate)}</div>
                        <div className="cs-kpi-cell__label">Recupero</div>
                        <div className="cs-kpi-cell__desc">Cuánto de la deuda generada ya se cobró</div>
                        <div className="cs-kpi-cell__sub">{money(health.debtPaid)} de {money(health.debtOriginal)}</div>
                    </div>
                </div>
            </div>

            {/* ── Antigüedad de la deuda ── */}
            <div className="cs-card">
                <div className="cs-card__header">
                    <div className="cs-card__title">¿Qué tan vieja es la deuda?</div>
                    <div className="cs-card__desc">
                        Deuda pendiente por antigüedad. Lo que pasa los 60 días va en rojo: a partir de ahí, cobrar deja
                        de ser cuestión de esperar.
                    </div>
                </div>

                {aging.length === 0 ? (
                    <div className="cs-empty cs-empty--compact">
                        <span>No hay deuda pendiente en {currency} para el período elegido.</span>
                    </div>
                ) : (
                    <>
                        <div className="cs-card__chart-body">
                            <ReactECharts option={agingOption} style={{ height: Math.max(180, aging.length * 52) }} theme={chartTheme} opts={{ renderer: 'svg' }} />
                        </div>
                        <div className="cs-card__body">
                            <div className="cs-table-wrap">
                                <table className="cs-table">
                                    <thead>
                                        <tr>
                                            <th>Antigüedad</th>
                                            <th className="cs-table__amount">Pendiente</th>
                                            <th className="cs-table__amount">Deudas</th>
                                            <th className="cs-table__amount">% del pendiente</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {aging.map((row) => (
                                            <tr key={row.bucket}>
                                                <td>
                                                    {row.label}
                                                    {row.isRisk && <> <span className="fp-tag fp-tag--bad">en riesgo</span></>}
                                                </td>
                                                <td className="cs-table__amount">{money(row.amount)}</td>
                                                <td className="cs-table__amount">{row.debtCount}</td>
                                                <td className="cs-table__amount">{Functions.formatPercent(row.share)}</td>
                                            </tr>
                                        ))}
                                        <tr className="fp-total">
                                            <td>Total pendiente</td>
                                            <td className="cs-table__amount">{money(pendingTotal)}</td>
                                            <td className="cs-table__amount">{health.pendingDebtCount}</td>
                                            <td className="cs-table__amount">100%</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* ── Movimiento en el tiempo ── */}
            <div className="cs-card">
                <div className="cs-card__header">
                    <div className="cs-card__title">Entradas y salidas</div>
                    <div className="cs-card__desc">
                        Lo que entró y salió cada período, con el neto acumulado. Es plata movida, no facturación.
                    </div>
                </div>
                <div className="cs-card__chart-body">
                    {series.length > 0 ? (
                        <ReactECharts option={seriesOption} style={{ height: 300 }} theme={chartTheme} opts={{ renderer: 'svg' }} />
                    ) : (
                        <div className="cs-empty cs-empty--compact"><span>Sin movimientos en {currency} para el período elegido.</span></div>
                    )}
                </div>
            </div>

            <div className="cs-two-col">
                {/* ── Evolución de la deuda ── */}
                <div className="cs-card">
                    <div className="cs-card__header">
                        <div className="cs-card__title">Deuda: la que nace y la que se cobra</div>
                        <div className="cs-card__desc">
                            Si las barras naranjas le ganan seguido a las verdes, la deuda crece más rápido de lo que se
                            cobra. La línea punteada es qué porcentaje se recupera.
                        </div>
                    </div>
                    <div className="cs-card__chart-body">
                        {debtSeries.length > 0 ? (
                            <ReactECharts option={debtOption} style={{ height: 300 }} theme={chartTheme} opts={{ renderer: 'svg' }} />
                        ) : (
                            <div className="cs-empty cs-empty--compact"><span>Sin deuda registrada en el período.</span></div>
                        )}
                    </div>
                </div>

                {/* ── Demora de cobro ── */}
                <div className="cs-card">
                    <div className="cs-card__header">
                        <div className="cs-card__title">¿Está tardando más en entrar la plata?</div>
                        <div className="cs-card__desc">
                            Días promedio entre que se genera la deuda y se cobra. Si la curva sube, la cobranza se está
                            estirando aunque el total cobrado no baje.
                        </div>
                    </div>
                    <div className="cs-card__chart-body">
                        {delaySeries.length > 0 ? (
                            <ReactECharts option={delayOption} style={{ height: 300 }} theme={chartTheme} opts={{ renderer: 'svg' }} />
                        ) : (
                            <div className="cs-empty cs-empty--compact"><span>Todavía no hay cobros suficientes para medir la demora.</span></div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Quién debe ── */}
            <div className="cs-card">
                <div className="cs-card__header">
                    <div className="cs-card__title">Quién te debe</div>
                    <div className="cs-card__desc">
                        Los mayores deudores del período, por monto pendiente. Sirve para saber a quién llamar primero.
                    </div>
                </div>
                <div className="cs-card__body">
                    {topDebtors.length === 0 ? (
                        <div className="cs-empty cs-empty--compact"><span>Nadie tiene deuda pendiente en {currency}.</span></div>
                    ) : (
                        <div className="cs-table-wrap">
                            <table className="cs-table">
                                <thead>
                                    <tr>
                                        <th>Deudor</th>
                                        <th className="cs-table__amount">Pendiente</th>
                                        <th className="cs-table__amount">Cobrado</th>
                                        <th className="cs-table__amount">Deudas</th>
                                        <th>Peso en la deuda</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {topDebtors.map((row) => (
                                        <tr key={row.entityIdentifier}>
                                            <td>
                                                <div className="fp-entity">
                                                    <span className="fp-entity__name">{resolveEntityName(row.entityIdentifier)}</span>
                                                    <span className="fp-entity__meta">Original: {money(row.totalOriginalAmount)}</span>
                                                </div>
                                            </td>
                                            <td className="cs-table__amount"><strong>{money(row.totalPendingAmount)}</strong></td>
                                            <td className="cs-table__amount">{money(row.totalPaidAmount)}</td>
                                            <td className="cs-table__amount">{row.debtCount}</td>
                                            <td>
                                                <div className="fp-share">
                                                    <div className="cs-progress__bar">
                                                        <div className="cs-progress__fill" style={{ width: `${Math.min(row.sharePercentage, 100)}%` }} />
                                                    </div>
                                                    <span className="fp-share__value">{Functions.formatPercent(row.sharePercentage)}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default CashBlockComponent;
